import { callApi } from "../../../ApiHelper/ApiHelper";
import { apiUrl } from "../../../ApiHelper/ApiUrl";

export const MOCKS = {
  category: true,
  code: true,
  extras: true,
  photo: true,
  counts: true,
  stock: true,
  duplicateCheck: true,
};

export const ITEM_CATEGORIES = [
  {
    key: "supply",
    type: "Supply",
    label: "Supply",
    prefix: "SUP",
    badgeClass: "bg-blue-25",
    groupLabel: "Expense Head",
    taxCodeLabel: "HSN Code",
    description: "Physical goods that are purchased and stocked",
  },
  {
    key: "service",
    type: "Service",
    label: "Service",
    prefix: "SRV",
    badgeClass: "bg-green-25",
    groupLabel: "Service Type",
    taxCodeLabel: "SAC Code",
    description: "Work carried out by vendors or contractors",
  },
  {
    key: "general",
    type: "General",
    label: "General",
    prefix: "GEN",
    badgeClass: "bg-grey-25",
    groupLabel: "General Expense Head",
    taxCodeLabel: "HSN Code",
    description: "Overheads and other general expenses",
  },
];

export const GST_OPTIONS = [0, 5, 12, 18, 28];

export const RATE_BASIS_OPTIONS = [
  "Per job",
  "Per visit",
  "Per kW",
  "Per month",
  "Lumpsum",
];

export const TDS_SECTION_OPTIONS = ["194C", "194J", "None"];

export const getCategoryMeta = (key) =>
  ITEM_CATEGORIES.find(
    (cat) => cat.key === key || cat.type === key
  ) || ITEM_CATEGORIES[2];

// ---------------------------------------------------------------------------
// Mock persistence: extra fields in localStorage, photos in-memory
// ---------------------------------------------------------------------------
const EXTRAS_STORAGE_KEY = "itemMasterMockExtras";
const photoStore = new Map();

const readExtras = () => {
  try {
    return JSON.parse(localStorage.getItem(EXTRAS_STORAGE_KEY)) || {};
  } catch {
    return {};
  }
};

export const saveItemExtras = (itemId, extras) => {
  if (!itemId) return;
  try {
    const all = readExtras();
    all[itemId] = { ...all[itemId], ...extras };
    localStorage.setItem(EXTRAS_STORAGE_KEY, JSON.stringify(all));
  } catch {
    // quota exceeded — mock data only, safe to drop
  }
};

export const getItemPhoto = (itemId) => photoStore.get(String(itemId)) || null;

export const setItemPhoto = (itemId, objectUrl) => {
  if (!itemId) return;
  const existing = photoStore.get(String(itemId));
  if (existing && existing !== objectUrl) URL.revokeObjectURL(existing);
  if (objectUrl) photoStore.set(String(itemId), objectUrl);
  else photoStore.delete(String(itemId));
};

// ---------------------------------------------------------------------------
// Photo validation / mock upload
// ---------------------------------------------------------------------------
const PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];
const PHOTO_MAX_BYTES = 6 * 1024 * 1024;

export const validatePhotoFile = (file) => {
  if (!file) return null;
  if (!PHOTO_TYPES.includes(file.type)) {
    return "Only JPG, PNG or WebP images are allowed";
  }
  if (file.size > PHOTO_MAX_BYTES) {
    return "Photo must be 6MB or smaller";
  }
  return null;
};

// Mock upload: returns a local object URL. Real endpoint will be a multipart
// POST returning { status, data: { photoUrl } }.
export const uploadItemPhoto = (file) => {
  const error = validatePhotoFile(file);
  if (error) return Promise.reject(new Error(error));
  return Promise.resolve(URL.createObjectURL(file));
};

// ---------------------------------------------------------------------------
// Item decoration (category / code / extras / photo on top of API response)
// ---------------------------------------------------------------------------
export const decorateItem = (item) => {
  const typeName = item?.expensehead?.expenseheadtype?.name;
  // Data-migration note: items with no expense-head type default to General
  const meta = getCategoryMeta(item?.category || typeName || "General");
  const extras = readExtras()[item?.id] || {};
  return {
    ...item,
    category: item?.category || meta.key,
    categoryLabel: meta.label,
    code: item?.code || `${meta.prefix}-${String(item?.id).padStart(4, "0")}`,
    gst: item?.gst ?? extras.gst ?? "",
    standardRate: item?.rate ?? item?.standardRate ?? extras.standardRate ?? "",
    description: item?.description ?? extras.description ?? "",
    make: item?.make ?? extras.make ?? "",
    model: item?.model ?? extras.model ?? "",
    minStock: item?.minStock ?? extras.minStock ?? "",
    isSerialized: item?.isSerialized ?? extras.isSerialized ?? false,
    warrantyMonths: item?.warrantyMonths ?? extras.warrantyMonths ?? "",
    rateBasis: item?.rateBasis ?? extras.rateBasis ?? "",
    tdsSection: item?.tdsSection ?? extras.tdsSection ?? "",
    photoUrl: item?.photoUrl || getItemPhoto(item?.id),
  };
};

// ---------------------------------------------------------------------------
// Counts — derived client-side until backend exposes a counts endpoint.
// Planned shape: { all, supply, service, general }
// ---------------------------------------------------------------------------
export const fetchItemCounts = () => {
  const countFor = (type) =>
    callApi(
      apiUrl.get_items + `pageNumber=1&pageSize=1${type ? `&type=${type}` : ""}`,
      { method: "GET" }
    )
      .then((res) =>
        res?.status === true ? res?.data?.pageInfo?.total || 0 : 0
      )
      .catch(() => 0);

  return Promise.all([
    countFor(""),
    countFor("Supply"),
    countFor("Service"),
    countFor("General"),
  ]).then(([all, supply, service, general]) => ({
    all,
    supply,
    service,
    general,
  }));
};

// ---------------------------------------------------------------------------
// Duplicate name check — client-side against the fetched list until the
// backend exposes a check endpoint. Planned: GET check-item-name?name=&type=
// -> { status, data: { exists: boolean } }
// ---------------------------------------------------------------------------
export const isDuplicateName = (name, items, excludeId = null) => {
  if (!name) return false;
  const needle = name.trim().toLowerCase();
  return (items || []).some(
    (item) =>
      item?.id !== excludeId &&
      String(item?.name || "").trim().toLowerCase() === needle
  );
};

// ---------------------------------------------------------------------------
// Stock across sites — mock, built for 200+ sites. Planned endpoint:
// GET item-stock?itemId=&search=&pageNumber=&pageSize=
// -> { status, data: { summary: { totalQuantity, sitesWithStock, totalSites },
//                      stock: [{ siteId, siteName, quantity, unit }],
//                      pageInfo: { total, totalPages } } }
// Only sites with non-zero stock are returned, largest quantity first.
// ---------------------------------------------------------------------------
const MOCK_SITE_COUNT = 230;
const MOCK_SITE_REGIONS = [
  "Jodhpur",
  "Bikaner",
  "Jaisalmer",
  "Barmer",
  "Phalodi",
  "Pokhran",
  "Nagaur",
  "Suratgarh",
];
const MOCK_SITE_TYPES = ["Solar Park", "Wind Farm", "Hybrid", "Substation"];

const buildMockStock = (item) => {
  const seed = Number(item?.id) || 1;
  const rows = [];
  for (let siteId = 1; siteId <= MOCK_SITE_COUNT; siteId++) {
    // deterministic pseudo-random qty; roughly a third of sites hold stock
    const qty = (seed * siteId * 7919) % 97;
    if (qty % 3 !== 0) continue;
    rows.push({
      siteId,
      siteName: `${MOCK_SITE_REGIONS[siteId % MOCK_SITE_REGIONS.length]} ${MOCK_SITE_TYPES[siteId % MOCK_SITE_TYPES.length]} ${Math.ceil(siteId / 8)}`,
      quantity: qty,
      unit: item?.unit || "",
    });
  }
  return rows.sort((a, b) => b.quantity - a.quantity);
};

export const fetchStockAcrossSites = (
  item,
  { search = "", pageNumber = 1, pageSize = 20 } = {}
) => {
  const url = `${apiUrl.get_item_stock_across_sites}itemId=${item?.id}&pageNumber=${pageNumber}&pageSize=${pageSize}${search ? `&search=${search}` : ""}`;
  return callApi(url, { method: "GET" }).then((res) => {
    if (res?.status === true) {
      const dataObj = res.data || {};
      const total = dataObj.total ?? 0;
      return {
        summary: {
          totalQuantity: dataObj.totalStock ?? 0,
          sitesWithStock: dataObj.sitesCount ?? 0,
          totalSites: dataObj.totalSites ?? 0,
        },
        stock: dataObj.data?.map((row) => ({
          siteId: row?.siteId,
          siteName: row?.siteName,
          quantity: row?.stock ?? 0,
          unit: item?.unit || "",
        })) || [],
        pageInfo: {
          total: total,
          totalPages: Math.ceil(total / pageSize) || 1,
        },
      };
    }
    return {
      summary: { totalQuantity: 0, sitesWithStock: 0, totalSites: 0 },
      stock: [],
      pageInfo: { total: 0, totalPages: 1 },
    };
  });
};
